import React from 'react';
import { useIntl } from 'react-intl';
import { Coins } from '@phosphor-icons/react';
import { FilterSwitch } from '../design-system/FilterSwitch';
import { useSalaryDisplayPreference } from '../contexts/SalaryDisplayPreferenceContext';
import { useFormatMessage } from '../i18n-review/client/Msg';

/**
 * Page-level control for switching job card salaries between the converted monthly-EUR figure
 * (default, matches the search index's sortable/filterable range) and the as-advertised original
 * currency and pay period. Client-only preference (see SalaryDisplayPreferenceContext) — renders
 * the same "converted" state during SSR/first paint as every visitor gets, so no hydration
 * mismatch even though a returning visitor's stored choice applies right after mount.
 */
export function SalaryDisplayToggle({ className = '' }: { className?: string }): React.ReactElement {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { mode, toggle } = useSalaryDisplayPreference();
  const isOriginal = mode === 'original';
  const label = formatMessage({
    id: 'salaryDisplayToggle.label',
    defaultMessage: 'Show original currency',
    description: 'Label for the page-level toggle switching job card salaries between converted EUR and as-advertised original currency',
  });

  return (
    <div className={`flex items-center justify-center gap-3 ${className}`}>
      <Coins className="h-4 w-4 text-[#7A6E5F] dark:text-[#B8A48C]" aria-hidden />
      <label htmlFor="salary-display-toggle" className="text-sm font-medium text-[#3D3529] dark:text-[#C4A882] select-none">
        {label}
      </label>
      <FilterSwitch id="salary-display-toggle" checked={isOriginal} onCheckedChange={toggle} aria-label={label} />
    </div>
  );
}
